// src/components/profile/ProfileTabs.jsx

/*
====================================================
ProfileTabs Component

Purpose:
Controls profile gallery tabs.

Tabs:
1. Pics (User Posts)
2. Collection (Saved Posts)

Props:
userId → used to fetch posts of the profile user
====================================================
*/

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfilePosts } from "../../hooks/useProfilePosts";
import { Grid2X2, Bookmark, Loader2, ArrowLeft } from "lucide-react";
import axiosInstance from "../../APIs/axiosInstance";
import { useEffect } from "react";
import ShareButton from "../Post/ShareButton";

const ProfileTabs = ({ user, isMe }) => {
  const [activeTab, setActiveTab] = useState("posts");

  return (
    <div className="mt-4">

      {/* Tabs */}
      <div className="relative flex justify-center border-b border-white/10">
        <button
          onClick={() => setActiveTab("posts")}
          className={`flex items-center gap-2 px-10 py-3 text-sm font-medium
            transition relative
            ${activeTab === "posts" ? "text-white" : "text-white/40 hover:text-white/60"}`}
        >
          <Grid2X2 className="w-4 h-4" />
          Pics
        </button>

        {isMe && (
          <button
            onClick={() => setActiveTab("saved")}
            className={`flex items-center gap-2 px-10 py-3 text-sm font-medium
              transition relative
              ${activeTab === "saved" ? "text-white" : "text-white/40 hover:text-white/60"}`}
          >
            <Bookmark className="w-4 h-4" />
            Collection
          </button>
        )}

        <div
          className={`absolute bottom-0 h-0.5 bg-white transition-all duration-300
            ${isMe ? "w-1/2" : "w-full"}
            ${activeTab === "posts" ? "left-0" : "left-1/2"}`}
        />
      </div>

      {/* Content */}
      <div className="mt-4 px-1.5">
        {activeTab === "posts" && <ProfilePosts userId={user?.id} />}
        {activeTab === "saved" && isMe && <SavedPosts />}
      </div>
    </div>
  );
};

// Posts grid — same
const ProfilePosts = ({ userId }) => {
  const { posts, loading, error } = useProfilePosts(userId);
  const navigate = useNavigate();

  if (loading) return (
    <div className="columns-2 gap-1 space-y-1">
      {[...Array(6)].map((_, i) => (
        <div key={i}
          className="break-inside-avoid bg-white/5 rounded-xl animate-pulse mb-2"
          style={{ height: `${180 + (i % 3) * 40}px` }}
        />
      ))}
    </div>
  );

  if (error) return (
    <p className="text-red-400 text-center py-10">Failed to load posts</p>
  );

  if (posts.length === 0) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Grid2X2 className="w-12 h-12 text-white/10" />
      <p className="text-white/20 text-sm">No posts yet</p>
    </div>
  );

  return (
    <div className="columns-2 gap-1 space-y-1">
      {posts.map((post) => (
        <div
          key={post.id}
          onClick={() => navigate(`/post/${post.id}`)}
          className="break-inside-avoid relative overflow-hidden rounded-xl
            cursor-pointer group mb-2"
        >
          <img
            src={post.image}
            alt="post"
            className="w-full object-cover group-hover:scale-105
              transition duration-300"
            style={{ minHeight: "140px" }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50
            transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100
              transition flex gap-4 text-white text-sm font-semibold">
              <span>❤️ {post.likes_count || 0}</span>
              <span>💬 {post.comments_count || 0}</span>
            </div>
          </div>
          {/* Share icon — top-right corner */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-1.5 right-1.5 z-10 bg-black/40 rounded-full"
          >
            <ShareButton postId={post.id} caption={post.caption} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ✅ Saved posts — boards list + board detail view
const SavedPosts = () => {
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boardItems, setBoardItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Fetch all boards
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        const res = await axiosInstance.get("/boards/");
        setBoards(res.data?.results || res.data || []);
      } catch (err) {
        console.error("Boards fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBoards();
  }, []);

  // ✅ Fetch board detail with items when board selected
  const handleBoardClick = async (board) => {
    setSelectedBoard(board);
    setLoadingItems(true);
    try {
      const res = await axiosInstance.get(`/boards/${board.id}/`);
      setBoardItems(res.data?.items || []);
    } catch (err) {
      console.error("Board items fetch error:", err);
    } finally {
      setLoadingItems(false);
    }
  };

  // ✅ Back to boards list
  const handleBack = () => {
    setSelectedBoard(null);
    setBoardItems([]);
  };

  if (loading) return (
    <div className="columns-2 gap-1 space-y-1">
      {[...Array(4)].map((_, i) => (
        <div key={i}
          className="break-inside-avoid bg-white/5 rounded-xl animate-pulse mb-2 h-40"
        />
      ))}
    </div>
  );

  // ✅ Board Detail View — 2 column grid of saved posts
  if (selectedBoard) {
    return (
      <div>
        {/* Back header */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-white/50 hover:text-white
            transition mb-4 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm">Back to collections</span>
        </button>

        {/* Board name */}
        <div className="mb-4">
          <h3 className="text-white font-bold text-lg">{selectedBoard.name}</h3>
          <p className="text-white/30 text-xs mt-0.5">
            {boardItems.length} saved posts
          </p>
        </div>

        {/* Loading */}
        {loadingItems ? (
          <div className="columns-2 gap-1 space-y-1">
            {[...Array(4)].map((_, i) => (
              <div key={i}
                className="break-inside-avoid bg-white/5 rounded-xl animate-pulse mb-2"
                style={{ height: `${160 + (i % 3) * 40}px` }}
              />
            ))}
          </div>
        ) : boardItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Bookmark className="w-12 h-12 text-white/10" />
            <p className="text-white/20 text-sm">No posts saved in this board</p>
          </div>
        ) : (
          /* ✅ Pinterest 2-column masonry grid */
          <div className="columns-2 gap-1 space-y-1">
            {boardItems.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(`/post/${item.post?.id}`)}
                className="break-inside-avoid relative overflow-hidden rounded-xl
                  cursor-pointer group mb-2"
              >
                <img
                  src={item.post?.image}
                  alt={item.post?.caption || "saved post"}
                  className="w-full object-cover group-hover:scale-105
                    transition duration-300"
                  style={{ minHeight: "140px" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50
                  transition-all duration-300 flex items-end p-3">
                  <div className="opacity-0 group-hover:opacity-100
                    transition-opacity duration-300 w-full">
                    {item.post?.caption && (
                      <p className="text-white/80 text-xs line-clamp-2">
                        {item.post.caption}
                      </p>
                    )}
                    <p className="text-white/40 text-xs mt-1">
                      @{item.post?.username || item.post?.author?.username}
                    </p>
                  </div>
                </div>
                {/* Share icon — top-right corner */}
                {item.post?.id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-1.5 right-1.5 z-10 bg-black/40 rounded-full"
                  >
                    <ShareButton postId={item.post.id} caption={item.post.caption} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ✅ Boards list view
  if (boards.length === 0) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Bookmark className="w-12 h-12 text-white/10" />
      <p className="text-white/20 text-sm">No saved posts yet</p>
    </div>
  );

  return (
    <div className="columns-2 gap-1 space-y-1">
      {boards.map((board) => (
        <div
          key={board.id}
          onClick={() => handleBoardClick(board)}
          className="break-inside-avoid rounded-xl overflow-hidden
            bg-white/5 border border-white/10 cursor-pointer
            hover:bg-white/10 transition mb-2"
        >
          {board.cover_image ? (
            <img
              src={board.cover_image}
              alt={board.name}
              className="w-full h-32 object-cover"
            />
          ) : (
            <div className="w-full h-32 bg-white/5 flex items-center justify-center">
              <Bookmark className="w-8 h-8 text-white/10" />
            </div>
          )}
          <div className="px-3 py-2">
            <p className="text-white text-sm font-medium truncate">
              {board.name}
            </p>
            <p className="text-white/30 text-xs">
              {board.items_count || 0} posts
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProfileTabs;